import Fuse, { IFuseOptions } from "fuse.js";
import { useCallback, useMemo } from "react";

type BaseItem<V> =
  | V
  | (V & {
      pattern?: RegExp;
      alwaysInclude?: boolean;
    });

const hasAlwaysInclude = <V>(item: BaseItem<V>): boolean =>
  Boolean(
    item &&
      typeof item === "object" &&
      "alwaysInclude" in item &&
      item.alwaysInclude,
  );

export const useFuseSearch = <V>(
  items: BaseItem<V>[],
  options: IFuseOptions<V>,
) => {
  const fuse = useMemo(() => {
    const itemsWithoutPattern = items.filter(
      (item) =>
        !item ||
        typeof item !== "object" ||
        !("pattern" in item) ||
        !item.pattern,
    );
    return new Fuse<V>(itemsWithoutPattern, options);
  }, [items, options]);

  return useCallback(
    (query: string): V[] => {
      // Items marked with alwaysInclude should always appear at the top
      const alwaysIncludeItems = items.filter(hasAlwaysInclude);

      if (query) {
        const filteredItems = fuse
          .search(query)
          .map(({ item }) => item)
          .filter((item) => !hasAlwaysInclude(item));
        const patternMatchedItems = items.filter(
          (item) =>
            item &&
            typeof item === "object" &&
            "pattern" in item &&
            item.pattern?.test(query) &&
            !hasAlwaysInclude(item),
        );

        return [
          ...alwaysIncludeItems,
          ...filteredItems,
          ...patternMatchedItems,
        ];
      } else {
        return items;
      }
    },
    [fuse, items],
  );
};
